/**
 * @module SAOrderDialog
 * @description Order Dialog 主入口 - ESM 模块
 * 
 * @example
 * // 基础用法 - 默认表单
 * SAOrderDialog.open();
 * 
 * @example
 * // 自定义标题和描述
 * SAOrderDialog.open({
 *   title: 'Contact Us',
 *   description: 'Fill out the form below'
 * });
 * 
 * @example
 * // 自定义 HTML 标题
 * SAOrderDialog.open({
 *   titleHtml: '<h2 class="custom">Get in <span class="highlight">Touch</span></h2>',
 *   description: 'We would love to hear from you'
 * });
 * 
 * @example
 * // 自定义内容（不渲染表单）
 * SAOrderDialog.open({
 *   title: 'Thank You!',
 *   description: null,
 *   content: '<div class="success-message"><p>Your order has been submitted successfully!</p></div>'
 * });
 * 
 * @example
 * // 自定义表单字段
 * SAOrderDialog.open({
 *   title: 'Quick Contact',
 *   fields: [
 *     { name: 'name', label: 'Name', required: true, type: 'text', row: 1 },
 *     { name: 'email', label: 'Email', required: true, type: 'email', row: 1 },
 *     { name: 'subject', label: 'Subject', required: false, type: 'text', row: 2 },
 *     { name: 'message', label: 'Message', required: true, type: 'textarea', row: 3 }
 *   ],
 *   checkboxes: [
 *     { id: 'consent', name: 'consent', required: true, html: 'I agree to the <a href="/terms">Terms</a>' }
 *   ],
 *   submitText: 'Send Message'
 * });
 * 
 * @example
 * // 完全自定义关闭按钮和提交按钮
 * SAOrderDialog.open({
 *   closeBtnHtml: '<button class="my-close-btn">×</button>',
 *   submitHtml: '<button type="submit" class="my-submit-btn">Go!</button>',
 *   showSubmitIcon: false
 * });
 * 
 * @example
 * // 自定义提交处理
 * SAOrderDialog.open({
 *   onSubmit: async (formData, form) => {
 *     const data = Object.fromEntries(formData);
 *     await myCustomApi.submit(data);
 *     alert('Submitted!');
 *   }
 * });
 */

import { OrderDialogRenderer } from './sa-order-dialog-renderer.js';
import { OrderDialogEventHandler } from './sa-order-dialog-event-handler.js';
import { OrderDialogValidator } from './sa-order-dialog-validator.js';
import { OrderDialogSubmitter } from './sa-order-dialog-submitter.js';
import { OrderDialog } from './sa-order-dialog-component.js';
import { OrderDialogManager } from './sa-order-dialog-manager.js';

const manager = new OrderDialogManager();

export const SAOrderDialog = {
  /**
   * 创建弹窗实例
   * @param {Object} config - 配置项
   * @returns {OrderDialog}
   */
  create: (config) => manager.create(config),
  
  /**
   * 打开弹窗
   * @param {string|Object} [idOrConfig] - 实例 ID 或配置对象
   * @param {Object} [config] - 配置对象（当第一个参数为 ID 时）
   * @returns {OrderDialog|null}
   */
  open: (idOrConfig, config) => idOrConfig === undefined 
    ? manager.open(manager.defaultId) 
    : manager.open(idOrConfig, config),
  
  /**
   * 关闭弹窗
   * @param {string} [id] - 实例 ID
   * @returns {boolean}
   */
  close: (id) => manager.close(id),
  
  /**
   * 更新弹窗配置
   * @param {string} id - 实例 ID
   * @param {Object} config - 配置对象
   * @returns {boolean}
   */
  update: (id, config) => manager.update(id, config),
  
  /**
   * 检查弹窗是否打开
   * @param {string} [id] - 实例 ID
   * @returns {boolean}
   */
  isOpen: (id) => manager.isOpen(id),
  
  /**
   * 获取弹窗实例
   * @param {string} [id] - 实例 ID
   * @returns {OrderDialog|null}
   */
  getInstance: (id) => manager.getInstance(id),
  
  /**
   * 销毁弹窗实例
   * @param {string} [id] - 实例 ID
   * @returns {boolean}
   */
  destroy: (id) => manager.destroy(id),
  
  /**
   * 添加事件监听
   * @param {string} eventName - 事件名称
   * @param {Function} handler - 处理函数
   * @param {string} [id] - 实例 ID
   * @returns {boolean}
   */
  on: (eventName, handler, id) => manager.on(eventName, handler, id),
  
  /**
   * 移除事件监听
   * @param {string} eventName - 事件名称
   * @param {Function} handler - 处理函数
   * @param {string} [id] - 实例 ID
   * @returns {boolean}
   */
  off: (eventName, handler, id) => manager.off(eventName, handler, id),
  
  /**
   * 获取管理器实例
   * @returns {OrderDialogManager}
   */
  getManager: () => manager,

  // 导出类供高级用法
  Component: OrderDialog,
  Renderer: OrderDialogRenderer,
  EventHandler: OrderDialogEventHandler,
  Validator: OrderDialogValidator,
  Submitter: OrderDialogSubmitter,
  Manager: OrderDialogManager
};

// 导出到全局命名空间
if (typeof globalThis !== 'undefined') {
  globalThis.SAOrderDialog = SAOrderDialog;
}

// 向后兼容
window.openOrderDialog = (options) => SAOrderDialog.open(options);

export default SAOrderDialog;
